import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Alert, TextField, Button, Container, Typography, Box, Paper, Stack, CircularProgress } from '@mui/material';

import DiactiveLogo from '../assets/images/diactive-logo.png';
import { getPasswordRequirements, validatePassword } from '../utils/ValidationUtils';
import { sendResetPasswordEmail, verifyPasswordResetToken, resetPassword } from '../services/FunctionService';
import { signInUser, signOutUser } from '../services/AuthService';

const ResetPasswordState = {
  Loading: 1,
  ShowForm: 2,
  Submitting: 3,
  Success: 4,
  Error: 5
};

const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [state, setState] = useState(ResetPasswordState.Loading);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  const onPasswordChange = (event) => setPassword(event.target.value);
  const onConfirmPasswordChange = (event) => setConfirmPassword(event.target.value);

  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      signOutUser();
      try {
        const { isValid, email } = await verifyPasswordResetToken(token);
        setEmail(email);
        if (isValid) {
          setState(ResetPasswordState.ShowForm);
        }
        else {
          setMessage('El enlace ha caducado, solicita uno nuevo');
          setMessageType('error');
          setState(ResetPasswordState.Error);
        }
      }
      catch (error) {
        setMessage(error.message);
        setMessageType('error');
        setState(ResetPasswordState.Error);
      }
    };

    validateToken();
  }, [token]);

  const onSubmit = async (event) => {
    event.preventDefault();
    setMessage('');

    const validationError = validatePassword(password, confirmPassword);
    if (validationError !== null) {
      setMessage(validationError);
      setMessageType('error');
      return;
    }

    setState(ResetPasswordState.Submitting);

    try {
      setState(ResetPasswordState.Submitting);
      await resetPassword(token, password);
      await signInUser(email, password);
      navigate('/');
    }
    catch (error) {
      const passwordRequirements = getPasswordRequirements(error.message);
      if (passwordRequirements.length > 0) {
      let userFriendlyMessage = 'La contraseña debe contener: ';
        userFriendlyMessage += passwordRequirements.join(', ').toLowerCase();
        setMessage(userFriendlyMessage);
        setMessageType('error');
        setState(ResetPasswordState.ShowForm);
      }
      else if (error.message === 'No tienes permiso para acceder a esta aplicación') {
        setMessageType('success');
        setMessage('La contraseña se ha establecido correctamente');
        setState(ResetPasswordState.Success);
      }
      else {
        setMessage(error.message);
        setMessageType('error');
        setState(ResetPasswordState.Error);
      }
    }
  };

  const onErrorButtonClick = async () => {
    if (email.length === 0) {
      navigate('/sign-in/reset');
      return;
    }

    try {
      setState(ResetPasswordState.Submitting);
      await sendResetPasswordEmail(email);
      setMessage("Se ha enviado un enlace nuevo a tu correo electrónico");
      setMessageType('success');
      setState(ResetPasswordState.Success);
    }
    catch (error) {
      setMessage(error.message);
      setMessageType('error');
    }
  };

  // Show only loading spinner during validation
  if (state === ResetPasswordState.Loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress size={45} />
      </Box>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ pt: 20 }}>
      <Paper elevation={3} sx={{ pt: 9, pb: 3, pl: 4, pr: 4, position: 'relative' }}>
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 125,
            height: 125,
            bgcolor: 'white',
            borderRadius: '50%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            boxShadow: 3,
          }}
        >
          <img src={DiactiveLogo} alt="Diactive Logo" style={{ width: '80%', height: '80%' }} />
        </Box>

        <Stack spacing={2} alignItems="center">
          <Typography variant="h5" component="h1" gutterBottom>
            Establecer contraseña
          </Typography>

          <Box sx={{ width: '100%' }}>
            {message && <Alert severity={messageType}>{message}</Alert>}
            {state === ResetPasswordState.ShowForm && (
              <>
                {message.length === 0 && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2" gutterBottom>
                      <strong>La contraseña debe contener:</strong>
                    </Typography>
                    {getPasswordRequirements().map((req, index) => (
                      <Typography key={index} variant="body2" sx={{ fontSize: '0.85rem' }}>
                        • {req}
                      </Typography>
                    ))}
                  </Alert>
                )}
                <form onSubmit={onSubmit}>
                  <TextField
                    label="Nueva contraseña"
                    type="password"
                    value={password}
                    onChange={onPasswordChange}
                    fullWidth
                    margin="normal"
                    autoFocus
                  />
                  <TextField
                    label="Confirmar contraseña"
                    type="password"
                    value={confirmPassword}
                    onChange={onConfirmPasswordChange}
                    fullWidth
                    margin="normal"
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    sx={{ mt: 2 }}
                  >
                    Establecer contraseña
                  </Button>
                </form>
              </>
            )}

            {state === ResetPasswordState.Error && (
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={onErrorButtonClick}
                sx={{ mt: 2 }}
              >
                {email.length > 0 ? "Solicitar nuevo enlace" : "Restaurar contraseña"}
              </Button>
            )}

            {state === ResetPasswordState.Submitting && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5, mb: 3 }}>
                <CircularProgress size={45} />
              </Box>
            )}
          </Box>
        </Stack>
      </Paper>
    </Container>
  );
};

export default ResetPassword;